import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  FormHelperText,
  InputAdornment,
  IconButton
} from '@mui/material';
import {
  Language as WebsiteIcon,
  Help as HelpIcon,
  Campaign as CampaignIcon
} from '@mui/icons-material';

const CAMPAIGN_GOALS = [
  { value: 'product_launch', label: 'Product/Service Launch' },
  { value: 'brand_awareness', label: 'Brand Awareness' },
  { value: 'event_promotion', label: 'Event Promotion' },
  { value: 'sales_promotion', label: 'Sales/Promotion' },
  { value: 'content_promotion', label: 'Newsletter/Content' },
];

const CampaignForm = ({ 
  onSubmit, 
  initialData = {}, 
  isLoading = false,
  showBackToProfiles = false,
  onBackToProfiles = () => {}
}) => {
  const [formData, setFormData] = useState({
    campaignName: initialData.campaignName || '',
    websiteUrl: initialData.websiteUrl || '',
    campaignGoal: initialData.campaignGoal || '',
    description: initialData.description || ''
  });
  
  const [errors, setErrors] = useState({});

  const validateUrl = (url) => {
    try {
      new URL(url);
      return url.startsWith('http://') || url.startsWith('https://');
    } catch {
      return false;
    }
  };

  const handleInputChange = (field) => (event) => {
    const value = event.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    
    const newErrors = {};
    
    // Validate campaign name
    if (!formData.campaignName.trim()) {
      newErrors.campaignName = 'Campaign name is required';
    } else if (formData.campaignName.length > 100) {
      newErrors.campaignName = 'Campaign name must be less than 100 characters';
    }
    
    // Validate website URL
    if (!formData.websiteUrl.trim()) {
      newErrors.websiteUrl = 'Website URL is required';
    } else if (!validateUrl(formData.websiteUrl)) {
      newErrors.websiteUrl = 'Please enter a valid website URL (including https://)';
    }
    
    // Validate campaign goal
    if (!formData.campaignGoal) {
      newErrors.campaignGoal = 'Please select a campaign goal';
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    onSubmit(formData);
  };

  const handleUrlBlur = () => {
    if (formData.websiteUrl && !formData.websiteUrl.startsWith('http')) {
      setFormData(prev => ({
        ...prev,
        websiteUrl: 'https://' + prev.websiteUrl
      }));
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
      <Typography variant="h6" gutterBottom>
        Campaign Details
      </Typography>
      
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Enter your campaign details and we'll analyze your website to create personalized content
      </Typography>

      <TextField
        fullWidth
        label="Campaign Name"
        value={formData.campaignName}
        onChange={handleInputChange('campaignName')}
        error={!!errors.campaignName}
        helperText={errors.campaignName || 'Give your campaign a memorable name'}
        placeholder="e.g., Spring Product Launch 2024"
        sx={{ mb: 3 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <CampaignIcon color="action" />
            </InputAdornment>
          ),
        }}
        disabled={isLoading}
      />

      <TextField
        fullWidth
        label="Website URL"
        value={formData.websiteUrl}
        onChange={handleInputChange('websiteUrl')}
        onBlur={handleUrlBlur}
        error={!!errors.websiteUrl}
        helperText={errors.websiteUrl || 'Enter your complete website URL (e.g., https://yourbusiness.com)'}
        placeholder="https://yourbusiness.com"
        sx={{ mb: 3 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <WebsiteIcon color="action" />
            </InputAdornment>
          ),
        }}
        disabled={isLoading}
      />

      <FormControl fullWidth sx={{ mb: 3 }} error={!!errors.campaignGoal}>
        <InputLabel>Campaign Goal</InputLabel>
        <Select
          value={formData.campaignGoal}
          onChange={handleInputChange('campaignGoal')}
          label="Campaign Goal"
          disabled={isLoading}
        >
          {CAMPAIGN_GOALS.map((goal) => (
            <MenuItem key={goal.value} value={goal.value}>
              {goal.label}
            </MenuItem>
          ))}
        </Select>
        {errors.campaignGoal && (
          <FormHelperText>{errors.campaignGoal}</FormHelperText>
        )}
        {!errors.campaignGoal && (
          <FormHelperText>Choose the primary goal for your campaign</FormHelperText>
        )}
      </FormControl>

      <TextField
        fullWidth
        label="Additional Context (Optional)"
        value={formData.description}
        onChange={handleInputChange('description')}
        multiline
        rows={3}
        error={!!errors.description}
        helperText={errors.description || 'Add any specific details about your campaign (optional)'}
        placeholder="e.g., Launching our new product line for small businesses..."
        sx={{ mb: 4 }}
        disabled={isLoading}
      />

      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
        {showBackToProfiles && (
          <Button
            variant="outlined"
            onClick={onBackToProfiles}
            disabled={isLoading}
            size="medium"
          >
            ← Back to Profile Selection
          </Button>
        )}
        
        <Button
          type="submit"
          variant="contained"
          size="large"
          disabled={isLoading}
          sx={{ minWidth: 200 }}
        >
          {isLoading ? 'Analyzing Website...' : 'Analyze Website & Create Campaign'}
        </Button>
      </Box>

      <Box sx={{ mt: 2, textAlign: 'center' }}>
        <Typography variant="caption" color="text.secondary">
          This process usually takes 30-60 seconds
        </Typography>
      </Box>
    </Box>
  );
};

export default CampaignForm; 